// Listing-request endpoint functions. Async, call `sellerApi`, throw `AppError`
// (mapped by the interceptor), and return DTOs — the parser runs in the
// mutation hook. The create call is multipart/form-data: the title-deed file
// plus the collected wizard fields. FormData is built here so the mutation and
// container stay clean; axios sets the multipart boundary automatically.
import { sellerApi } from '@core/api/httpClient'
import { toE164Mobile, toEmiratesIdDigits } from '../utils/ownerContactUtils'
import { listingRequestEndpointPaths } from './listingRequestEndpointPaths'
import type {
  ApiEnvelope,
  DocumentExtractionDto,
  InitiateSigningDto,
  ListingRequestDto,
  ListingRequestDetailDto,
  ListingRequestEventDto,
  ListingRequestListDto,
  ListingRequestStatusCountsDto,
  SellerPayoutSummaryDto,
  ListingRequestSummaryDto,
  OnboardingFeeQuoteDto,
  PaginatedEnvelope,
  PropertyAgreementDto,
  ShareSignatoryLinkDto,
} from './listingRequestContracts'
import type {
  CreateListingRequestPayload,
  DeclineAgreementSignaturePayload,
  ExtractListingRequestDocumentsPayload,
  ListingRequestDraft,
  OwnerDraft,
  RespondToPricePayload,
  ReviseListingRequestPayload,
  SaveListingRequestDraftPayload,
  WithdrawListingRequestPayload,
} from '../types/listingRequestTypes'

// Query params accepted by the paginated list endpoint. `statuses` is omitted
// when empty (the server treats a missing filter as "all").
export interface GetListingRequestsParams {
  page: number
  limit: number
  statuses?: string[]
}

// Optional fields are appended only when non-empty — the server validates
// present fields, so sending an empty string would fail format checks.
function appendOptional(formData: FormData, key: string, value: string) {
  const trimmed = value.trim()
  if (trimmed) formData.append(key, trimmed)
}

// Owners travel as a JSON string field (an array can't ride multipart cleanly);
// the server parses + validates it. Percentage is coerced to a number here; the
// mobile number is normalised to E.164 (the tel input renders it spaced) and the
// Emirates ID to bare digits (the seller may type the card's dashes).
function serializeOwners(owners: OwnerDraft[]): string {
  return JSON.stringify(
    owners.map((owner) => ({
      fullName: owner.fullName.trim(),
      ownershipPercentage: Number(owner.ownershipPercentage),
      email: owner.email.trim(),
      mobileNumber: toE164Mobile(owner.mobileNumber),
      emiratesId: toEmiratesIdDigits(owner.emiratesId),
      ownerType: owner.ownerType,
      isPrimary: owner.isPrimary,
    })),
  )
}

// Shared multipart serializer for create + revise. The field set is identical;
// the only difference is `titleDeed`, which is required on create and optional
// on revise (the server keeps the existing deed when it's omitted).
function toFormData(payload: CreateListingRequestPayload | ReviseListingRequestPayload): FormData {
  const formData = new FormData()
  if (payload.titleDeed) formData.append('titleDeed', payload.titleDeed)
  // Only ever sent alongside `isRented: true` — the server rejects an agreement
  // attached to a property the seller says is not rented.
  if (payload.rentalAgreement) formData.append('rentalAgreement', payload.rentalAgreement)
  formData.append('ownershipRole', payload.ownershipRole)
  // Required, so appended unconditionally — `appendOptional` would silently drop it
  // and the request would land with a null type that can never be promoted.
  formData.append('propertyType', payload.propertyType)
  formData.append('propertyTypeOnDeed', payload.propertyTypeOnDeed)
  formData.append('isRented', String(payload.isRented))
  // Appended unconditionally, not via `appendOptional`: an unticked box is a real
  // declaration, and dropping it would leave a revise carrying whatever the request
  // already said.
  formData.append('isFreehold', String(payload.isFreehold))
  formData.append('isResidential', String(payload.isResidential))
  formData.append('isMortgageFree', String(payload.isMortgageFree))
  formData.append('isDldBlockFree', String(payload.isDldBlockFree))
  formData.append('acceptedTerms', String(payload.acceptedTerms))
  // Terms only ride along on a rented property. Sent unconditionally they would be
  // blanks on a non-rented request, which the server rejects outright
  // (PROPERTY_RENT_DETAILS_NOT_APPLICABLE) rather than ignoring. No monthly figure
  // is sent: the server derives it from the annual one.
  if (payload.isRented) {
    appendOptional(formData, 'tenantName', payload.tenantName)
    appendOptional(formData, 'annualRentAmount', payload.annualRentAmount)
    appendOptional(formData, 'leaseStart', payload.leaseStart)
    appendOptional(formData, 'leaseEnd', payload.leaseEnd)
  }
  formData.append('certificateNumber', payload.certificateNumber.trim())
  formData.append('propertyName', payload.propertyName.trim())
  formData.append('askingPrice', payload.askingPrice.trim())
  formData.append('currencyCode', payload.currencyCode)
  formData.append('owners', serializeOwners(payload.owners))
  appendOptional(formData, 'totalAreaSqm', payload.totalAreaSqm)
  appendOptional(formData, 'builtUpAreaSqm', payload.builtUpAreaSqm)
  appendOptional(formData, 'propertyNumber', payload.propertyNumber)
  appendOptional(formData, 'buildingName', payload.buildingName)
  appendOptional(formData, 'locationArea', payload.locationArea)
  appendOptional(formData, 'addressLine1', payload.addressLine1)
  appendOptional(formData, 'city', payload.city)
  appendOptional(formData, 'countryId', payload.countryId)
  appendOptional(formData, 'plotNumber', payload.plotNumber)
  appendOptional(formData, 'municipalityNumber', payload.municipalityNumber)
  appendOptional(formData, 'certificateYear', payload.certificateYear)
  appendOptional(formData, 'buildingNumber', payload.buildingNumber)
  appendOptional(formData, 'mortgageStatus', payload.mortgageStatus ?? '')
  appendOptional(formData, 'mortgageeName', payload.mortgageeName)
  appendOptional(formData, 'deedIssueDate', payload.deedIssueDate)
  appendOptional(formData, 'registrationNumber', payload.registrationNumber)
  appendOptional(formData, 'registrationDate', payload.registrationDate)
  appendOptional(formData, 'purchasePrice', payload.purchasePrice)
  return formData
}

// ── Drafts ────────────────────────────────────────────────────────────────

// Present-only owner fields: a draft row is whatever the seller has typed, and
// the server format-checks every field it is sent, so a blank must be left out
// rather than posted as an empty string.
function serializeDraftOwners(owners: OwnerDraft[]): string {
  return JSON.stringify(
    owners.map((owner) => {
      const percentage = owner.ownershipPercentage.trim()
      return Object.fromEntries(
        Object.entries({
          fullName: owner.fullName.trim(),
          ownershipPercentage: percentage === '' ? undefined : Number(percentage),
          email: owner.email.trim(),
          mobileNumber: toE164Mobile(owner.mobileNumber),
          emiratesId: toEmiratesIdDigits(owner.emiratesId),
          ownerType: owner.ownerType,
          isPrimary: owner.isPrimary,
        }).filter(([, value]) => value !== undefined && value !== ''),
      )
    }),
  )
}

const DRAFT_TEXT_FIELDS = [
  'certificateNumber',
  'propertyName',
  'askingPrice',
  'totalAreaSqm',
  'builtUpAreaSqm',
  'propertyNumber',
  'buildingName',
  'buildingNumber',
  'locationArea',
  'addressLine1',
  'city',
  'countryId',
  'plotNumber',
  'municipalityNumber',
  'certificateYear',
  'mortgageeName',
  'deedIssueDate',
  'registrationNumber',
  'registrationDate',
  'purchasePrice',
] as const satisfies readonly (keyof ListingRequestDraft)[]

const DRAFT_RENT_FIELDS = [
  'tenantName',
  'annualRentAmount',
  'leaseStart',
  'leaseEnd',
] as const satisfies readonly (keyof ListingRequestDraft)[]

// Everything the seller has filled in, and nothing they have not: every field is
// optional on a draft, and a blank sent as "" would fail the server's format
// checks. The declarations are always sent — an unticked box is still an answer.
// `acceptedTerms` is never sent: terms are accepted on submit, not saved.
function toDraftFormData(payload: SaveListingRequestDraftPayload): FormData {
  const { draft } = payload
  const formData = new FormData()
  if (payload.titleDeed) formData.append('titleDeed', payload.titleDeed)
  if (draft.isRented && payload.rentalAgreement) {
    formData.append('rentalAgreement', payload.rentalAgreement)
  }
  if (draft.ownershipRole) formData.append('ownershipRole', draft.ownershipRole)
  if (draft.propertyType) formData.append('propertyType', draft.propertyType)
  if (draft.propertyTypeOnDeed) formData.append('propertyTypeOnDeed', draft.propertyTypeOnDeed)
  // An unanswered rented question saves as "not rented" — the server has no null
  // for it, and a draft is not a declaration either way.
  formData.append('isRented', String(draft.isRented === true))
  formData.append('isFreehold', String(draft.isFreehold))
  formData.append('isResidential', String(draft.isResidential))
  formData.append('isMortgageFree', String(draft.isMortgageFree))
  formData.append('isDldBlockFree', String(draft.isDldBlockFree))
  formData.append('currencyCode', draft.currencyCode)
  DRAFT_TEXT_FIELDS.forEach((field) => appendOptional(formData, field, draft[field]))
  // Held apart from `DRAFT_TEXT_FIELDS`: it is a closed enum, not free text, and
  // `null` is "not read", which `appendOptional` drops rather than sending.
  appendOptional(formData, 'mortgageStatus', draft.mortgageStatus ?? '')
  if (draft.isRented) {
    DRAFT_RENT_FIELDS.forEach((field) => appendOptional(formData, field, draft[field]))
  }
  if (draft.owners.length > 0) formData.append('owners', serializeDraftOwners(draft.owners))
  return formData
}

export async function createListingRequestDraft(
  payload: SaveListingRequestDraftPayload,
): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.post<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.LISTING_REQUEST_DRAFTS,
    toDraftFormData(payload),
  )
  return data.data
}

export async function updateListingRequestDraft(
  id: string,
  payload: SaveListingRequestDraftPayload,
): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.patch<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.listingRequestDraft(id),
    toDraftFormData(payload),
  )
  return data.data
}

// Submit a saved draft. The full form, exactly like a revise: either file may be
// left out to keep the one already saved on the draft.
export async function submitListingRequestDraft(
  id: string,
  payload: ReviseListingRequestPayload,
): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.post<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.listingRequestSubmit(id),
    toFormData(payload),
  )
  return data.data
}

export async function discardListingRequestDraft(id: string): Promise<void> {
  await sellerApi.delete(listingRequestEndpointPaths.listingRequest(id))
}

export async function createListingRequest(
  payload: CreateListingRequestPayload,
): Promise<ListingRequestDto> {
  const { data } = await sellerApi.post<ApiEnvelope<ListingRequestDto>>(
    listingRequestEndpointPaths.LISTING_REQUESTS,
    toFormData(payload),
  )
  return data.data
}

// Title Deed step: send a just-picked document for extraction so the wizard can
// be pre-filled. Either file may go alone. Same multipart field names as create.
// Nothing is stored server-side — the files are sent again with the submit.
export async function extractListingRequestDocuments(
  payload: ExtractListingRequestDocumentsPayload,
): Promise<DocumentExtractionDto> {
  const formData = new FormData()
  if (payload.titleDeed) formData.append('titleDeed', payload.titleDeed)
  if (payload.rentalAgreement) formData.append('rentalAgreement', payload.rentalAgreement)
  const { data } = await sellerApi.post<ApiEnvelope<DocumentExtractionDto>>(
    listingRequestEndpointPaths.LISTING_REQUEST_DOCUMENT_EXTRACTIONS,
    formData,
  )
  return data.data
}

// ── Phase B seller actions ────────────────────────────────────────────────
// All three return the full refreshed detail (same shape as GET :id), so the
// mutation hooks run `parseListingRequestDetail` on the result.

// Accept / reject the admin's proposed price. JSON body; not a financial write.
export async function respondToListingRequestPrice(
  id: string,
  payload: RespondToPricePayload,
): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.post<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.listingRequestPriceResponse(id),
    payload,
  )
  return data.data
}

// Withdraw the seller's own request. Idempotent server-side.
export async function withdrawListingRequest(
  id: string,
  payload: WithdrawListingRequestPayload,
): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.post<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.listingRequestWithdraw(id),
    payload,
  )
  return data.data
}

// Edit + resubmit after a revision request. Multipart PUT on the detail path;
// the title deed is optional (existing deed kept when omitted).
export async function reviseListingRequest(
  id: string,
  payload: ReviseListingRequestPayload,
): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.put<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.listingRequest(id),
    toFormData(payload),
  )
  return data.data
}

// Paginated list of the seller's own requests. Unwraps the paginated envelope
// into rows + pagination meta for the parser.
export async function getListingRequests(
  params: GetListingRequestsParams,
): Promise<ListingRequestListDto> {
  const { data } = await sellerApi.get<PaginatedEnvelope<ListingRequestSummaryDto>>(
    listingRequestEndpointPaths.LISTING_REQUESTS,
    {
      params: {
        page: params.page,
        limit: params.limit,
        // Comma-joined rather than repeated: axios would otherwise send
        // `status[]=…`, which the endpoint doesn't parse.
        ...(params.statuses?.length ? { status: params.statuses.join(',') } : {}),
      },
    },
  )
  return { items: data.data, pagination: data.pagination }
}

export async function getListingRequestStatusCounts(): Promise<ListingRequestStatusCountsDto> {
  const { data } = await sellerApi.get<ApiEnvelope<ListingRequestStatusCountsDto>>(
    listingRequestEndpointPaths.LISTING_REQUEST_STATUS_COUNTS,
  )
  return data.data
}

export async function getSellerPayoutSummary(): Promise<SellerPayoutSummaryDto> {
  const { data } = await sellerApi.get<ApiEnvelope<SellerPayoutSummaryDto>>(
    listingRequestEndpointPaths.LISTING_REQUEST_PAYOUT_SUMMARY,
  )
  return data.data
}

export async function getListingRequest(id: string): Promise<ListingRequestDetailDto> {
  const { data } = await sellerApi.get<ApiEnvelope<ListingRequestDetailDto>>(
    listingRequestEndpointPaths.listingRequest(id),
  )
  return data.data
}

export async function getListingRequestTimeline(id: string): Promise<ListingRequestEventDto[]> {
  const { data } = await sellerApi.get<ApiEnvelope<ListingRequestEventDto[]>>(
    listingRequestEndpointPaths.listingRequestTimeline(id),
  )
  return data.data
}

export async function getListingRequestAgreements(id: string): Promise<PropertyAgreementDto[]> {
  const { data } = await sellerApi.get<ApiEnvelope<PropertyAgreementDto[]>>(
    listingRequestEndpointPaths.listingRequestAgreements(id),
  )
  return data.data
}

// ── UAE Pass agreement signing ────────────────────────────────────────────

// Start a signing session for one signatory; returns the URL to redirect to.
export async function initiateAgreementSignature(
  id: string,
  agreementId: string,
  signatoryId: string,
): Promise<InitiateSigningDto> {
  const { data } = await sellerApi.post<ApiEnvelope<InitiateSigningDto>>(
    listingRequestEndpointPaths.agreementSignInitiate(id, agreementId, signatoryId),
  )
  return data.data
}

// Mint a secure public signing-page link for one signatory, to share with the
// owner (e.g. a co-owner who signs on their own device).
export async function shareAgreementSignatureLink(
  id: string,
  agreementId: string,
  signatoryId: string,
): Promise<ShareSignatoryLinkDto> {
  const { data } = await sellerApi.post<ApiEnvelope<ShareSignatoryLinkDto>>(
    listingRequestEndpointPaths.agreementShareLink(id, agreementId, signatoryId),
  )
  return data.data
}

// Finalise a signing session after the signer returns from UAE Pass. Returns
// the refreshed agreement (parsed via `parseAgreement` in the hook).
export async function completeAgreementSignature(
  id: string,
  agreementId: string,
  signatoryId: string,
): Promise<PropertyAgreementDto> {
  const { data } = await sellerApi.post<ApiEnvelope<PropertyAgreementDto>>(
    listingRequestEndpointPaths.agreementSignComplete(id, agreementId, signatoryId),
  )
  return data.data
}

// Decline to sign an agreement (optional reason).
export async function declineAgreementSignature(
  id: string,
  agreementId: string,
  signatoryId: string,
  payload: DeclineAgreementSignaturePayload,
): Promise<PropertyAgreementDto> {
  const { data } = await sellerApi.post<ApiEnvelope<PropertyAgreementDto>>(
    listingRequestEndpointPaths.agreementSignDecline(id, agreementId, signatoryId),
    payload,
  )
  return data.data
}

/**
 * Price the onboarding fees against a price the seller is considering. Read-only
 * — nothing is created, and the wizard re-asks whenever the price settles.
 *
 * Each area is sent only when the seller has given one: they are read by a fee
 * quoted per unit, and an empty string would fail the server's format check.
 */
export async function getOnboardingFeeQuote(
  askingPrice: string,
  totalAreaSqm: string,
  builtUpAreaSqm: string,
): Promise<OnboardingFeeQuoteDto> {
  const { data } = await sellerApi.get<ApiEnvelope<OnboardingFeeQuoteDto>>(
    listingRequestEndpointPaths.ONBOARDING_FEE_QUOTE,
    {
      params: {
        askingPrice,
        ...(totalAreaSqm ? { totalAreaSqm } : {}),
        ...(builtUpAreaSqm ? { builtUpAreaSqm } : {}),
      },
    },
  )
  return data.data
}
